import { Component, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from "@angular/forms";
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { RewardsService } from '../../../services/rewards.service';
import { FileUpload } from 'primeng/fileupload';
import { environment } from "./../../../../environments/environment";
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
@Component({
  selector: 'app-rewards-add',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss'],
  providers: [ConfirmationService, MessageService, RewardsService]
})

export class RewardAddComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  staticPath: string;
  submitted = false;
  id: string;
  isAddMode: boolean;
  pagetitle: string;
  rewardForm: FormGroup;
  public loggedInUser;
  public userId;
  public userRoleId;
  public rewardModule;
  public loggedInUserPermissions;
  public rewardPermissions: any[] = [];
  sampleRewordImagesLinkArray: any[] = [];
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  @ViewChild('fileUpload', { read: ElementRef }) fileUploadEl!: ElementRef;
  cropDialog: boolean = false;
  imageChangedEvent: any = '';
  croppedImage: any = '';
  imageQueue: any[] = [];
  currentImageIndex: number = 0;
  croppedImageMaxSize = 1000000;
  imageSize = 1000000;
  croppedImageSize = 0;
  croppedImageError: string | null = null;
  scale = 1;
  transform: ImageTransform = {};
  batchCropCompleted = false;
  isApplyingCrop = false;
  isSubmitting = false;
  private batchStartImageCount = 0;
  private cropApplyUnlockTimer: any = null;

  constructor(
    private rewardsService: RewardsService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private tokenStorageService: TokenStorageService
  ) { }

  ngOnInit() {

    this.loggedInUser = this.tokenStorageService.getUser();
    this.userId = this.loggedInUser.userdata.id;
    this.userRoleId = this.loggedInUser.userdata.role;
    this.rewardModule = "Rewards Management";
    this.loggedInUserPermissions = this.tokenStorageService.getUserPermissions();
    this.loggedInUserPermissions.forEach(permission => {
      if (permission.module == this.rewardModule) {
        let permissionObj = {
          moduleName: permission.module,
          status: permission.status,
          permission: permission.permission
        }
        this.rewardPermissions.push(permissionObj);
      }
    });
    if (this.rewardPermissions.length == 0) {
      this.router.navigateByUrl('/manage/dashboard')
    }
    this.rewardPermissions = this.rewardPermissions[0]['permission'];
    this.staticPath = environment.uploadUrl;
    this.pagetitle = 'Add Rewards';
    this.id = this.route.snapshot.paramMap.get("id");
    this.isAddMode = !this.id;
    this.rewardForm = this.formBuilder.group({
      reward_name: ['', [Validators.required]],
      coins: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      size: [true, Validators.required],
      description: ['', [Validators.required]],
      reward_images: this.formBuilder.array([]),
      status: [true, Validators.required],
      disable: [false],
    });
  }

  get rewardImagesFormArray(): FormArray {
    return this.rewardForm.get('reward_images') as FormArray;
  }

  get f() {
    return this.rewardForm.controls;
  }

  onFileSelect(event: any) {
    const files: File[] = Array.from(event.files || []);
    if (files.length === 0) {
      return;
    }
    const validFiles: File[] = [];
    files.forEach((file: File) => {
      if (file.size > this.imageSize) {
        this.messageService.add({
          key: 'toastmsg',
          severity: 'error',
          summary: 'Image Too Large',
          detail: `${file.name} exceeds 1 MB`,
          life: 4000
        });
      } else {
        validFiles.push(file);
      }
    });
    this.clearFileInput();
    if (validFiles.length === 0) {
      return;
    }

    this.batchStartImageCount = this.rewardImagesFormArray.length;
    this.batchCropCompleted = false;
    this.isApplyingCrop = false;
    this.imageQueue = [...validFiles];
    this.currentImageIndex = 0;
    this.loadCropperImage();
  }

  loadCropperImage(fromQueueTransition: boolean = false) {
    this.clearCropApplyUnlockTimer();
    const file = this.imageQueue[this.currentImageIndex];
    if (!file) {
      this.cropDialog = false;
      return;
    }
    this.croppedImage = null;
    this.resetCropValidation();
    this.resetZoom();
    this.isApplyingCrop = fromQueueTransition;
    this.imageChangedEvent = {
      target: {
        files: [file]
      }
    };
    this.cropDialog = true;
    if (fromQueueTransition) {
      const expectedIndex = this.currentImageIndex;
      this.cropApplyUnlockTimer = setTimeout(() => {
        if (this.cropDialog && this.currentImageIndex === expectedIndex) {
          this.isApplyingCrop = false;
        }
        this.cropApplyUnlockTimer = null;
      }, 600);
    }
  }

  get cropDialogTitle(): string {
    return 'Crop & Adjust Reward Photo';
  }

  get cropDialogSubtitle(): string {
    const progress = this.imageQueue.length
      ? `Photo ${this.currentImageIndex + 1} of ${this.imageQueue.length} - `
      : '';
    return `${progress}position and scale your image for a 1000 x 1000 crop`;
  }

  get cropPreviewLabel(): string {
    return '1000 x 1000 px - High Quality JPEG';
  }

  zoomIn() {
    this.scale = Math.min(3, Number((this.scale + 0.1).toFixed(1)));
    this.transform = {
      ...this.transform,
      scale: this.scale
    };
  }

  zoomOut() {
    this.scale = Math.max(1, Number((this.scale - 0.1).toFixed(1)));
    this.transform = {
      ...this.transform,
      scale: this.scale
    };
  }

  resetZoom() {
    this.scale = 1;
    this.transform = {
      ...this.transform,
      scale: 1
    };
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
    this.croppedImageSize = this.getBase64SizeBytes(this.croppedImage);
    this.croppedImageError = this.croppedImageSize > this.croppedImageMaxSize
      ? 'Cropped image size must not exceed 1 MB.'
      : null;
  }

  onCropImageLoadFailed() {
    this.croppedImage = null;
    this.croppedImageSize = 0;
    this.croppedImageError = 'This image could not be loaded. Please choose a valid image file.';
  }

  getBase64SizeBytes(base64: string): number {
    if (!base64) return 0;
    return Math.round(
      (base64.length * (3 / 4)) -
      (base64.split(',')[0].length + 1)
    );
  }

  applyCrop() {
    if (this.isApplyingCrop || this.croppedImageError || !this.croppedImage) return;
    const currentFile = this.imageQueue[this.currentImageIndex];
    if (!currentFile) {
      this.croppedImageError = 'The selected photo is no longer available. Cancel and choose it again.';
      return;
    }

    this.isApplyingCrop = true;
    const croppedFile = this.base64ToFile(
      this.croppedImage,
      this.getOutputFileName(currentFile.name)
    );
    if (croppedFile.size > this.croppedImageMaxSize) {
      this.croppedImageError = 'Cropped image size must not exceed 1 MB.';
      this.isApplyingCrop = false;
      return;
    }

    const rewardImage = this.formBuilder.group({
      file: this.croppedImage
    });
    this.rewardImagesFormArray.push(rewardImage);
    this.currentImageIndex++;
    if (this.currentImageIndex < this.imageQueue.length) {
      this.loadCropperImage(true);
    } else {
      this.finishUploadFlow();
    }
  }

  finishUploadFlow() {
    this.batchCropCompleted = true;
    this.cropDialog = false;
  }

  onCropCancel() {
    this.batchCropCompleted = false;
    this.cropDialog = false;
  }

  onDialogHide() {
    if (!this.batchCropCompleted) {
      this.rollbackCurrentBatch();
    }
    this.resetCropFlow();
  }

  private rollbackCurrentBatch() {
    while (this.rewardImagesFormArray.length > this.batchStartImageCount) {
      this.rewardImagesFormArray.removeAt(this.rewardImagesFormArray.length - 1);
    }
  }

  private resetCropFlow() {
    this.clearCropApplyUnlockTimer();
    this.imageQueue = [];
    this.currentImageIndex = 0;
    this.imageChangedEvent = null;
    this.croppedImage = null;
    this.resetCropValidation();
    this.resetZoom();
    this.batchStartImageCount = this.rewardImagesFormArray.length;
    this.batchCropCompleted = false;
    this.isApplyingCrop = false;
    this.clearFileInput();
  }

  private clearCropApplyUnlockTimer() {
    if (this.cropApplyUnlockTimer) {
      clearTimeout(this.cropApplyUnlockTimer);
      this.cropApplyUnlockTimer = null;
    }
  }

  private clearFileInput() {
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    const input = this.fileUploadEl?.nativeElement?.querySelector('input[type="file"]') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  resetCropValidation() {
    this.croppedImageError = null;
    this.croppedImageSize = 0;
  }

  removeRewardImages(index: number) {
    this.rewardImagesFormArray.removeAt(index);
  }

  base64ToFile(data: string, filename: string): File {
    const arr = data.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  private getOutputFileName(filename: string | undefined): string {
    const baseName = (filename || 'reward-photo').replace(/\.[^/.]+$/, '');
    return `${baseName}.jpg`;
  }

  onSubmit() {
    if (this.isSubmitting) {
      return;
    }
    this.submitted = true;
    if (this.rewardForm.invalid) {
      return;
    }
    if (this.rewardImagesFormArray.length === 0) {
      return;
    }
    var rewardData = this.rewardForm.value;
    rewardData = JSON.stringify(rewardData, null, 4);
    this.isSubmitting = true;
    this.rewardsService.addReward(rewardData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.messageService.add({
          key: 'toastmsg',
          severity: 'success',
          summary: 'Successful',
          detail: 'Rewards Added Successfully!!',
          life: 6000
        });
        setTimeout(() => {
          this.router.navigate(['/manage/rewards']);
        }, 2000);

      }, ((err) => {
        this.isSubmitting = false;
        this.messageService.add({
          key: 'toastmsg',
          severity: 'error',
          summary: "Error",
          detail: err.error.message,
          life: 6000
        });
      }));
  }

  backLink() {
    this.router.navigate(['/manage/rewards']);
  }

  ngOnDestroy() {
    this.clearCropApplyUnlockTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
